/// LeftSidebar: Resizable left panel with Palette/Project tabs.
/// Uses Radix Tabs for tab switching and pointer events for resize drag handle.

import { useState, useCallback, useRef } from "react";
import * as Tabs from "@radix-ui/react-tabs";
import { PanelLeftClose, PanelLeftOpen } from "lucide-react";
import { useUIStore } from "@/stores/uiStore";
import { ElementPalette } from "@/components/panels/ElementPalette";
import { ProjectPanel } from "@/components/panels/ProjectPanel";

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function LeftSidebar() {
  const visible = useUIStore((s) => s.leftSidebarVisible);
  const width = useUIStore((s) => s.leftSidebarWidth);
  const setWidth = useUIStore((s) => s.setLeftSidebarWidth);
  const toggle = useUIStore((s) => s.toggleLeftSidebar);

  const [activeTab, setActiveTab] = useState("palette");
  const dragRef = useRef<{ startX: number; startWidth: number } | null>(null);

  // Resize via pointer drag on right edge
  const handlePointerDown = useCallback(
    (e: React.PointerEvent) => {
      e.preventDefault();
      dragRef.current = { startX: e.clientX, startWidth: width };
      const target = e.currentTarget as HTMLElement;
      target.setPointerCapture(e.pointerId);
    },
    [width],
  );

  const handlePointerMove = useCallback(
    (e: React.PointerEvent) => {
      if (!dragRef.current) return;
      const delta = e.clientX - dragRef.current.startX;
      setWidth(dragRef.current.startWidth + delta);
    },
    [setWidth],
  );

  const handlePointerUp = useCallback(() => {
    dragRef.current = null;
  }, []);

  if (!visible) {
    return (
      <button
        type="button"
        className="flex h-full w-8 items-center justify-center border-r border-zinc-700 bg-zinc-800 text-zinc-500 hover:text-zinc-300 transition-colors"
        onClick={toggle}
        aria-label="Open left sidebar"
      >
        <PanelLeftOpen className="h-4 w-4" />
      </button>
    );
  }

  return (
    <div
      data-testid="left-sidebar"
      className="relative flex flex-col border-r border-zinc-700 bg-zinc-800 min-h-0 select-none"
      style={{ width: `${width}px`, minWidth: "160px", maxWidth: "480px" }}
    >
      {/* Header with collapse button */}
      <div className="flex items-center justify-between border-b border-zinc-700 px-2 py-1">
        <Tabs.Root value={activeTab} onValueChange={setActiveTab} className="flex-1">
          <Tabs.List className="flex gap-1">
            <Tabs.Trigger
              value="palette"
              className={`rounded px-2 py-0.5 text-xs transition-colors ${
                activeTab === "palette"
                  ? "bg-zinc-700 text-zinc-100"
                  : "text-zinc-400 hover:text-zinc-200"
              }`}
            >
              Palette
            </Tabs.Trigger>
            <Tabs.Trigger
              value="project"
              className={`rounded px-2 py-0.5 text-xs transition-colors ${
                activeTab === "project"
                  ? "bg-zinc-700 text-zinc-100"
                  : "text-zinc-400 hover:text-zinc-200"
              }`}
            >
              Project
            </Tabs.Trigger>
          </Tabs.List>
        </Tabs.Root>

        <button
          type="button"
          className="ml-1 rounded p-0.5 text-zinc-500 hover:text-zinc-300 transition-colors"
          onClick={toggle}
          aria-label="Collapse left sidebar"
        >
          <PanelLeftClose className="h-3.5 w-3.5" />
        </button>
      </div>

      {/* Tab content */}
      <div className="flex-1 min-h-0 overflow-hidden">
        {activeTab === "palette" && <ElementPalette />}
        {activeTab === "project" && <ProjectPanel />}
      </div>

      {/* Right-edge resize handle */}
      <div
        className="absolute right-0 top-0 bottom-0 w-1 cursor-col-resize hover:bg-blue-500/30 active:bg-blue-500/50 z-10"
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
      />
    </div>
  );
}
